import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Flag, 
  TrendingUp, 
  Heart, 
  MessageCircle,
  Crown,
  ArrowLeft,
  Search,
  Ban,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Tab components
const DashboardTab = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    <StatCard icon={Users} label="Total Users" value={stats.total_users} color="text-blue-400" />
    <StatCard icon={TrendingUp} label="Active (30d)" value={stats.active_users_30d} color="text-green-400" />
    <StatCard icon={Crown} label="Premium Users" value={stats.premium_users} color="text-gold" />
    <StatCard icon={Flag} label="Pending Reports" value={stats.pending_reports} color="text-red-400" />
    <StatCard icon={Heart} label="Total Matches" value={stats.total_matches} color="text-pink-400" />
    <StatCard icon={MessageCircle} label="Total Messages" value={stats.total_messages} color="text-purple-400" />
  </div>
);

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-dark-elevated rounded-xl p-4 border border-white/10">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-white/5 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-gray-400 text-xs font-body">{label}</p>
        <p className="text-white font-heading text-xl">{value?.toLocaleString() || 0}</p>
      </div>
    </div>
  </div>
);

const UsersTab = ({ users, loading, onAction, onSearch, onLoadMore, hasMore }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <div>
      {/* Search */}
      <form onSubmit={handleSearch} className="mb-4 flex gap-2">
        <Input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-dark-elevated border-white/10 text-white"
        />
        <Button type="submit" className="btn-gold">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      {/* Users list */}
      <div className="space-y-3">
        {users.map(user => (
          <div key={user.user_id} className="bg-dark-elevated rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-white font-body font-medium">{user.name}</p>
                  <p className="text-gray-400 text-sm">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user.is_premium && (
                  <span className="px-2 py-1 bg-gold/20 text-gold text-xs rounded-full">Premium</span>
                )}
                {user.is_suspended && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Suspended</span>
                )}
                {user.is_banned && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Banned</span>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-white/10 text-gray-300 hover:bg-white/5"
                onClick={() => onAction(user.user_id, 'warn')}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                Warn
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                onClick={() => onAction(user.user_id, 'suspend')}
              >
                <Ban className="w-3 h-3 mr-1" />
                Suspend
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => onAction(user.user_id, 'ban')}
              >
                <XCircle className="w-3 h-3 mr-1" />
                Ban
              </Button>
              {(user.is_suspended || user.is_banned) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                  onClick={() => onAction(user.user_id, 'unban')}
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Unban
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      )}

      {hasMore && !loading && (
        <Button
          onClick={onLoadMore}
          variant="outline"
          className="w-full mt-4 border-white/10 text-gray-300"
        >
          Load More
        </Button>
      )}
    </div>
  );
};

const ReportsTab = ({ reports, loading, onHandle }) => (
  <div className="space-y-3">
    {reports.length === 0 && !loading && (
      <div className="text-center py-8 text-gray-400">
        <Flag className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No pending reports</p>
      </div>
    )}

    {reports.map(report => (
      <div key={report.report_id} className="bg-dark-elevated rounded-xl p-4 border border-white/10">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-white font-body font-medium">
              Report: {report.reason.replace(/_/g, ' ')}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Reporter: {report.reporter?.name || 'Unknown'}
            </p>
            <p className="text-gray-400 text-sm">
              Reported: {report.reported_user?.name || 'Unknown'} ({report.reported_user?.email})
            </p>
            {report.description && (
              <p className="text-gray-300 text-sm mt-2 bg-white/5 p-2 rounded">
                &quot;{report.description}&quot;
              </p>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {new Date(report.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Reported user photos */}
        {report.reported_user?.photos?.length > 0 && (
          <div className="flex gap-2 mb-3">
            {report.reported_user.photos.slice(0, 3).map((photo, i) => (
              <img
                key={i}
                src={photo}
                alt="User"
                className="w-16 h-16 rounded-lg object-cover"
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-white/10 text-gray-300 hover:bg-white/5"
            onClick={() => onHandle(report.report_id, 'dismiss')}
          >
            Dismiss
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
            onClick={() => onHandle(report.report_id, 'warn')}
          >
            Warn User
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
            onClick={() => onHandle(report.report_id, 'suspend')}
          >
            Suspend
          </Button>
          <Button
            size="sm"
            className="text-xs bg-red-500 hover:bg-red-600"
            onClick={() => onHandle(report.report_id, 'ban')}
          >
            Ban User
          </Button>
        </div>
      </div>
    ))}

    {loading && (
      <div className="text-center py-4">
        <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    )}
  </div>
);

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      // Check if user is admin by trying to load stats
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
      setIsAdmin(true);
      
      // Load initial data
      await Promise.all([loadUsers(), loadReports()]);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        console.error('Admin load error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (search = '', page = 1) => {
    try {
      const response = await adminAPI.getUsers({ search, page, limit: 20 });
      if (page === 1) {
        setUsers(response.data.users);
      } else {
        setUsers(prev => [...prev, ...response.data.users]);
      }
      setHasMoreUsers(page < response.data.total_pages);
      setUsersPage(page);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadReports = async () => {
    try {
      const response = await adminAPI.getReports({ status: 'pending' });
      setReports(response.data.reports);
    } catch (error) {
      console.error('Failed to load reports:', error);
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      await adminAPI.userAction({
        user_id: userId,
        action: action,
        reason: `Admin action: ${action}`
      });
      toast.success(`User ${action} successful`);
      loadUsers('', usersPage);
      loadReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleReport = async (reportId, action) => {
    try {
      await adminAPI.handleReport(reportId, action);
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : 'handled'}`);
      loadReports();
      checkAdminAndLoad(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to handle report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center p-4">
        <div className="text-center">
          <Ban className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-white mb-2">Access Denied</h1>
          <p className="text-gray-400 font-body mb-6">You don&apos;t have admin privileges</p>
          <Button onClick={() => navigate('/discover')} className="btn-gold">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'reports', label: 'Reports', icon: Flag, badge: stats.pending_reports },
  ];

  return (
    <div className="min-h-screen bg-dark" data-testid="admin-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-4 py-4">
          <button
            onClick={() => navigate('/discover')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-xl text-gold-gradient">Admin Panel</h1>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-body transition-colors relative ${
                activeTab === tab.id ? 'text-gold' : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="absolute top-2 right-1/4 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="adminTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"
                />
              )}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'dashboard' && <DashboardTab stats={stats} />}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            loading={loading}
            onAction={handleUserAction}
            onSearch={(q) => loadUsers(q, 1)}
            onLoadMore={() => loadUsers('', usersPage + 1)}
            hasMore={hasMoreUsers}
          />
        )}
        {activeTab === 'reports' && (
          <ReportsTab
            reports={reports}
            loading={loading}
            onHandle={handleReport}
          />
        )}
      </div>
    </div>
  );
}
